import { useEffect, useState } from "react";
import client, { apiError } from "@/api/client";
import { PageHeader, Section } from "@/components/PageChrome";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { useUserNames } from "@/hooks/useUserNames";
import { toast } from "sonner";
import { Truck, ArrowRight } from "@phosphor-icons/react";

export default function Dispatch() {
  const [pending, setPending] = useState([]);
  const [transporters, setTransporters] = useState([]);
  const [form, setForm] = useState({ order_id: "", delivery_date: "", bill_no: "", bill_date: "", transporter_name: "", lr_no: "", lr_date: "" });
  const { nameFor } = useUserNames();

  const load = async () => {
    const [{ data }, { data: t }] = await Promise.all([
      client.get("/orders/pending-dispatch"),
      client.get("/transporters"),
    ]);
    setPending(data);
    setTransporters(t);
  };
  useEffect(() => { load(); }, []);

  const submit = async () => {
    if (!form.order_id || !form.delivery_date || !form.bill_no || !form.transporter_name)
      return toast.error("Fill all fields");
    try {
      await client.post("/orders/dispatch", form);
      toast.success("Dispatched ✓"); setForm({ order_id: "", delivery_date: "", bill_no: "", bill_date: "", transporter_name: "", lr_no: "", lr_date: "" }); load();
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div>
      <PageHeader title="Dispatch" subtitle="Stage 5 · Transport & Bill" />
      <Section>
        <div className="grid lg:grid-cols-3 gap-6">
          <div className="lg:col-span-1 bg-rose-50 border border-rose-200 border-l-4 border-l-rose-500 rounded-sm p-5">
            <div className="label-cap text-rose-800">New Dispatch Entry</div>
            <h3 className="font-display text-xl font-bold mt-1 text-rose-900 mb-4">Send order out</h3>

            <label className="label-cap">Order No</label>
            <Select value={form.order_id} onValueChange={(v)=>setForm({...form, order_id: v})}>
              <SelectTrigger data-testid="dispatch-order-select" className="rounded-sm mt-1 bg-white"><SelectValue placeholder="Select packed order"/></SelectTrigger>
              <SelectContent>
                {pending.map(o=><SelectItem key={o.id} value={o.id}>{o.order_no} — {o.retailer_name}</SelectItem>)}
              </SelectContent>
            </Select>

            <label className="label-cap block mt-4">Delivery Date</label>
            <Input data-testid="dispatch-date-input" type="date" value={form.delivery_date} onChange={(e)=>setForm({...form, delivery_date: e.target.value})} className="mt-1 bg-white"/>

            <label className="label-cap block mt-4">Bill No</label>
            <Input data-testid="dispatch-bill-input" value={form.bill_no} onChange={(e)=>setForm({...form, bill_no: e.target.value})} className="mt-1 bg-white"/>

            <label className="label-cap block mt-4">Bill Date</label>
            <Input data-testid="dispatch-bill-date-input" type="date" value={form.bill_date} onChange={(e)=>setForm({...form, bill_date: e.target.value})} className="mt-1 bg-white"/>

            <label className="label-cap block mt-4">Transporter Name</label>
            <Select value={form.transporter_name} onValueChange={(v)=>setForm({...form, transporter_name: v})}>
              <SelectTrigger data-testid="dispatch-transporter-select" className="rounded-sm mt-1 bg-white">
                <SelectValue placeholder={transporters.length ? "Select transporter" : "No transporters — add in master"} />
              </SelectTrigger>
              <SelectContent>
                {transporters.map(t => (
                  <SelectItem key={t.id} value={t.name}>{t.name}{t.phone ? ` · ${t.phone}` : ""}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {transporters.length === 0 && (
              <div className="text-xs text-rose-600 mt-1">Add transporters in Transporter Master first.</div>
            )}

            <label className="label-cap block mt-4">LR No</label>
            <Input data-testid="dispatch-lr-no-input" value={form.lr_no} onChange={(e)=>setForm({...form, lr_no: e.target.value})} className="mt-1 bg-white"/>

            <label className="label-cap block mt-4">LR Date</label>
            <Input data-testid="dispatch-lr-date-input" type="date" value={form.lr_date} onChange={(e)=>setForm({...form, lr_date: e.target.value})} className="mt-1 bg-white"/>

            <Button data-testid="dispatch-submit-btn" onClick={submit} className="w-full mt-5 bg-rose-600 hover:bg-rose-700 rounded-sm">
              <Truck size={16} weight="bold" className="mr-1"/> Dispatch <ArrowRight size={16} className="ml-1"/>
            </Button>
          </div>

          <div className="lg:col-span-2 bg-white border border-slate-200 rounded-sm">
            <div className="px-5 py-3 border-b border-slate-200"><div className="label-cap">Ready for Dispatch · {pending.length}</div></div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="text-left label-cap border-b border-slate-200 bg-slate-50">
                  <th className="px-4 py-2">Order No</th><th className="px-4 py-2">Retailer</th>
                  <th className="px-4 py-2">Pieces</th><th className="px-4 py-2">Boxes</th>
                  <th className="px-4 py-2">Weight</th>
                  <th className="px-4 py-2">Packed By</th><th className="px-4 py-2">Packed At</th>
                </tr></thead>
                <tbody>
                  {pending.map(o=>(
                    <tr key={o.id} className="border-b border-slate-100 hover:bg-rose-50">
                      <td className="px-4 py-2 font-mono">{o.order_no}</td>
                      <td className="px-4 py-2">{o.retailer_name}</td>
                      <td className="px-4 py-2 font-mono">{o.scanning?.total_pieces ?? o.scanning?.total_boxes ?? "—"}</td>
                      <td className="px-4 py-2 font-mono">{o.scanning?.total_boxes || "—"}</td>
                      <td className="px-4 py-2 font-mono">{o.scanning?.weight || "—"}</td>
                      <td className="px-4 py-2 text-xs">{nameFor(o.packing?.mobile)}</td>
                      <td className="px-4 py-2 text-xs text-slate-600">{o.packing && new Date(o.packing.at).toLocaleString()}</td>
                    </tr>
                  ))}
                  {pending.length===0 && <tr><td colSpan={7} className="px-4 py-10 text-center text-slate-500">Nothing to dispatch</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </Section>
    </div>
  );
}
